import { IDxfPoint } from './dxf-entities';

/** Decimal places a coordinate keeps in the file: a hundredth of a millimetre, below any saw. */
const COORD_FACTOR: number = 10 ** 2;
/** Decimal places of a value that is not a length — an angle, a ratio, a pattern scale. */
const REAL_FACTOR: number = 10 ** 6;
/** The owner handle of a record nobody owns. */
export const DXF_NO_OWNER: string = '0';

/** The file being written: group-code lines and the next free handle. */
export interface IDxfOut {
    lines: string[];
    nextHandle: number;
}

/** A fresh writer whose first handle is `firstHandle`. */
export function dxfOut(firstHandle: number = 1): IDxfOut {
    return { lines: [], nextHandle: firstHandle };
}

function formatRounded(value: number, factor: number): string {
    const rounded: number = Math.round(value * factor) / factor;
    const clean: number = rounded === 0 ? 0 : rounded;
    return Number.isInteger(clean) ? clean.toFixed(1) : String(clean);
}

/** A coordinate or a length: rounded to 0.01 mm, always with a decimal point, no `-0`. */
export function formatDxfNumber(value: number): string {
    return formatRounded(value, COORD_FACTOR);
}

/** A value that is not a length (an angle, a scale, a ratio): six decimal places. */
export function formatDxfReal(value: number): string {
    return formatRounded(value, REAL_FACTOR);
}

/** One group: its code line and its value line. */
export function pair(out: IDxfOut, code: number, value: string): void {
    out.lines.push(String(code), value);
}

/** A length group, rounded to the file's coordinate precision. */
export function coord(out: IDxfOut, code: number, value: number): void {
    pair(out, code, formatDxfNumber(value));
}

/** A real group that is not a length. */
export function real(out: IDxfOut, code: number, value: number): void {
    pair(out, code, formatDxfReal(value));
}

/** An integer group. */
export function int(out: IDxfOut, code: number, value: number): void {
    pair(out, code, String(Math.round(value)));
}

/** The next free handle, upper-case hexadecimal. */
export function takeHandle(out: IDxfOut): string {
    const handle: string = out.nextHandle.toString(16).toUpperCase();
    out.nextHandle += 1;
    return handle;
}

/** A 3D point on the drawing plane: X at `baseCode`, Y at `baseCode + 10`, Z = 0 at `baseCode + 20`. */
export function point(out: IDxfOut, baseCode: number, at: IDxfPoint): void {
    coord(out, baseCode, at[0]);
    coord(out, baseCode + 10, at[1]);
    coord(out, baseCode + 20, 0);
}

/** A 2D point: X at `baseCode`, Y at `baseCode + 10`. */
export function point2d(out: IDxfOut, baseCode: number, at: IDxfPoint): void {
    coord(out, baseCode, at[0]);
    coord(out, baseCode + 10, at[1]);
}

export function beginSection(out: IDxfOut, name: string): void {
    pair(out, 0, 'SECTION');
    pair(out, 2, name);
}

export function endSection(out: IDxfOut): void {
    pair(out, 0, 'ENDSEC');
}
